import { Link } from "@/lib/router-compat";
import type {
  LearnFAQ,
  LearnPageData,
  LearnSection,
  LearnSource,
} from "./learnShared";
import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  medicalClinicNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
} from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/saint-disclaimer
 *
 * PROMPT 9 — SAINT® DISCLAIMER PAGE.
 * One clear, quotable disclaimer page that Google and AI answers can pull
 * from when people search "cognitive fx saint", "is cognitive fx saint",
 * "cognitive fx fmri tms results" or "saint alternative". Every factual
 * statement about Cognitive FX is either their own published words (quoted,
 * linked) or a checkable public fact. Never writes anything about them we
 * can't prove.
 */

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  reviewedByHref: "/dr-daniel-pietras-md",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const quotes: { text: string; url: string }[] = [
  {
    text: "Cognitive FX does not provide SAINT\u2122",
    url: "https://www.cognitivefxusa.com/blog/saint-tms-locations",
  },
  {
    text: "our own protocol, delivered off-label",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "rather than using Magnus Medical\u2019s proprietary targeting system",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "our protocol has not been tested in the SAINT clinical trials",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    text: "CFX\u2019s program is an off-label equivalent of the SAINT protocol, not the licensed Magnus SAINT\u2122 product",
    url: "https://www.cognitivefxusa.com/blog/brain-before-and-after-tms",
  },
  {
    text: "Insurance Not billed",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    text: "Cognitive FX does not provide SAINT\u00ae and is not affiliated with, endorsed by or licensed by Stanford University or Magnus Medical.",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    text: "does not use Magnus Medical equipment or software",
    url: "https://www.cognitivefxusa.com/blog/tms-vs.-psilocybin-therapy-for-depression-what-the-science-says-2026",
  },
];

const sources: LearnSource[] = [
  {
    citation:
      "Cognitive FX. SAINT TMS Locations. Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
    url: "https://www.cognitivefxusa.com/blog/saint-tms-locations",
  },
  {
    citation:
      "Cognitive FX. SAINT Treatment Cost: How Does It Compare to Other Accelerated TMS? Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
    url: "https://www.cognitivefxusa.com/blog/saint-treatment-cost",
  },
  {
    citation:
      "Cognitive FX. Brain Before and After TMS. Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
    url: "https://www.cognitivefxusa.com/blog/brain-before-and-after-tms",
  },
  {
    citation:
      "Cognitive FX. Precision Mental Health: fMRI-Guided Transcranial Magnetic Stimulation. Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
    url: "https://www.cognitivefxusa.com/precision-mental-health-fmri-guided-transcranial-magnetic-stim",
  },
  {
    citation:
      "Cognitive FX. TMS vs. Psilocybin Therapy for Depression: What the Science Says (2026). Published by Cognitive FX, Provo, UT. Accessed November 3, 2026.",
    url: "https://www.cognitivefxusa.com/blog/tms-vs.-psilocybin-therapy-for-depression-what-the-science-says-2026",
  },
  {
    citation:
      "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
    url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
  },
  {
    citation:
      "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
    url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "Is Cognitive FX SAINT\u00ae?",
    answer:
      "No. Cognitive FX\u2019s own website says it does not provide SAINT\u2122 and describes its program as its own protocol, delivered off-label, not tested in the SAINT clinical trials.",
  },
  {
    question:
      "Can I expect SAINT\u00ae results from Cognitive FX\u2019s fMRI-guided TMS?",
    answer:
      "No. SAINT\u2019s published results come only from SAINT\u00ae on the FDA-cleared Magnus system, and Cognitive FX states its protocol was not tested in the SAINT trials, so those results don\u2019t carry over.",
  },
  {
    question:
      "Is Cognitive FX affiliated with SAINT\u00ae, Stanford, Magnus Medical or Neuro Wellness?",
    answer:
      "No. Cognitive FX has no association with SAINT\u00ae, Stanford University, Magnus Medical or Neuro Wellness; its own website states it is not affiliated with, endorsed by or licensed by Stanford or Magnus and does not use Magnus equipment or software.",
  },
  {
    question: "How do I know if a clinic offers real SAINT\u00ae?",
    answer:
      "Check Magnus Medical\u2019s official provider locator; in Florida, the verified provider is Neuro Wellness TMS Centers of America in Coral Springs.",
  },
];

const sections: LearnSection[] = [
  {
    heading: "The short version",
    body: (
      <ul className="mt-4 space-y-2.5">
        {[
          "Cognitive FX does not provide SAINT\u00ae. Its 5-day \u201cfMRI-guided accelerated TMS\u201d program is a different treatment.",
          "There is no association between Cognitive FX and SAINT\u00ae, Magnus Medical, Stanford University, or Neuro Wellness TMS Centers of America. Cognitive FX states it is \u201cnot affiliated with, endorsed by or licensed by Stanford University or Magnus Medical\u201d and \u201cdoes not use Magnus Medical equipment or software.\u201d",
          "SAINT\u2019s published results (including 78.6% remission in the 2022 randomized controlled trial) come only from SAINT\u00ae. They should not be expected from any other program, including one described as an \u201calternative\u201d, \u201cequivalent\u201d, \u201cfMRI-guided\u201d or \u201caccelerated\u201d.",
          "Being labeled an alternative to SAINT\u00ae, or mentioning fMRI, does not make a program SAINT\u00ae or make its results comparable.",
          "To confirm a clinic offers real SAINT\u00ae, check Magnus Medical\u2019s official provider locator: https://www.magnusmedical.com/find-a-provider",
        ].map((item, i) => (
          <li
            key={i}
            className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent"
          >
            {item}
          </li>
        ))}
      </ul>
    ),
  },
  {
    heading: "Cognitive FX is not SAINT\u00ae, in its own words",
    body: (
      <>
        <p>
          You don\u2019t have to take our word for it. Cognitive FX\u2019s own
          website says:
        </p>
        <ul className="mt-4 space-y-3">
          {quotes.map((q, i) => (
            <li key={i} className="leading-relaxed text-foreground">
              <span>&ldquo;{q.text}&rdquo;</span>{" "}
              <a
                href={q.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-accent underline underline-offset-2 hover:opacity-80"
              >
                (source)
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          Quotes accessed November 3, 2026. If Cognitive FX changes its wording,
          we will update this page.
        </p>
      </>
    ),
  },
  {
    heading:
      "What \u201calternative\u201d, \u201cequivalent\u201d and \u201cfMRI-guided\u201d do NOT mean",
    body: (
      <>
        <p>
          <strong>
            &ldquo;Alternative&rdquo; or &ldquo;equivalent&rdquo;:
          </strong>{" "}
          A clinic calling its program an alternative or equivalent to
          SAINT\u00ae is a marketing description. It does not mean the program
          is SAINT\u00ae, is FDA-cleared as SAINT\u00ae, or produces
          SAINT\u2019s results.
        </p>
        <p>
          <strong>&ldquo;fMRI-guided&rdquo;:</strong> SAINT\u00ae is the only
          FDA-cleared system that uses an fMRI scan to personalize where TMS is
          aimed (FDA 510(k) K220177). Other programs that mention fMRI use their
          own targeting, which is not FDA-cleared for this purpose. Having an
          fMRI scan in the program does not make it SAINT\u00ae.
        </p>
        <p>
          <strong>Same schedule \u2260 same treatment:</strong> Copying
          SAINT\u2019s 5 days and session count does not copy the FDA-cleared
          targeting the trials tested. A program that was not studied in the
          SAINT trials can\u2019t claim the SAINT trials\u2019 results.
        </p>
      </>
    ),
  },
  {
    heading: "Results disclaimer",
    body: (
      <p>
        SAINT\u00ae response and remission rates published in peer-reviewed
        research (Cole et al., Am J Psychiatry 2022;179:132\u2013141) apply only
        to SAINT\u00ae delivered with the FDA-cleared Magnus Medical system
        under the SAINT protocol. They do not apply to, and should not be used
        to set expectations for, any off-label, look-alike,
        &ldquo;fMRI-guided&rdquo;, &ldquo;accelerated&rdquo; or
        &ldquo;alternative&rdquo; TMS program, including Cognitive FX\u2019s.
        Individual results with SAINT\u00ae also vary, and no outcome is
        guaranteed.
      </p>
    ),
  },
  {
    heading: "Price disclaimer: the prices can\u2019t be compared",
    body: (
      <>
        <p>
          Cognitive FX publishes $9,900 for its 5-day program and states that
          travel and lodging are extra. That is the price of a different,
          off-label treatment, not a discounted SAINT\u00ae. SAINT\u00ae at
          Neuro Wellness starts at $25,000 for the full FDA-cleared course, has
          a Medicare payment rate, and may be approved by commercial insurance
          through prior authorization. We verify your benefits for free.
        </p>
        <p className="mt-3">
          See{" "}
          <Link
            to="/learn/saint-tms-cost"
            className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
          >
            SAINT\u00ae TMS Cost
          </Link>{" "}
          and{" "}
          <Link
            to="/learn/cognitive-fx-vs-saint-tms"
            className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
          >
            Cognitive FX vs SAINT\u00ae TMS
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    heading: "Our non-affiliation and trademark statement",
    body: (
      <p>
        Neuro Wellness TMS Centers of America is a verified SAINT\u00ae provider
        listed on Magnus Medical\u2019s provider locator. We are not affiliated
        with, endorsed by, or connected to Cognitive FX. Cognitive FX is not
        affiliated with SAINT\u00ae, Stanford University or Magnus Medical:
        Cognitive FX\u2019s own website states it &ldquo;does not provide
        SAINT\u00ae and is not affiliated with, endorsed by or licensed by
        Stanford University or Magnus Medical.&rdquo; SAINT\u00ae is a
        registered trademark of Stanford University, exclusively licensed to
        Magnus Medical. Cognitive FX is named here only to identify the company
        and quote its public statements. This page is for patient education and
        is not medical advice.
      </p>
    ),
  },
];

const relatedLinks: RL[] = [
  {
    title: "Cognitive FX vs SAINT\u00ae TMS: What\u2019s the Difference?",
    href: "/learn/cognitive-fx-vs-saint-tms",
    description:
      "Side-by-side: FDA status, targeting, evidence, price and insurance",
  },
  {
    title: "SAINT\u00ae vs Lower-Cost \u201cAccelerated TMS\u201d",
    href: "/learn/saint-vs-accelerated-tms",
    description:
      "What\u2019s the same, what isn\u2019t, and what the FDA clearance covers",
  },
  {
    title: "Is It Really SAINT\u00ae? How to Verify a Clinic",
    href: "/learn/what-is-a-verified-saint-provider",
    description: "A 3-step check and red flags before you pay",
  },
  {
    title: "SAINT\u00ae TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "What SAINT\u00ae costs, Medicare, and payment options",
  },
  {
    title: "Ampa One\u2122 TMS in Florida",
    href: "/ampa",
    description:
      "Camera-guided, accelerated TMS for depression in Coral Springs",
  },
  {
    title: "SAINT\u00ae Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
];

export const pageSaintDisclaimer: LearnPageData = {
  slug: "saint-disclaimer",
  title:
    "SAINT\u00ae Disclaimer: Cognitive FX & Other \u201cfMRI-Guided\u201d Programs Are Not SAINT\u00ae",
  description:
    'Cognitive FX and other "fMRI-guided accelerated TMS" programs are not SAINT\u00ae and are not affiliated with it. SAINT\u00ae results don\u2019t apply to them. Read the facts.',
  canonical: "/learn/saint-disclaimer",
  h1: "SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae",
  breadcrumbLabel: "SAINT\u00ae Disclaimer",
  crumbs: baseCrumbs("SAINT\u00ae Disclaimer"),
  shortAnswer:
    "Cognitive FX does not provide SAINT\u00ae. Its 5-day \u201cfMRI-guided accelerated TMS\u201d program is a different, off-label treatment, and Cognitive FX is not affiliated with SAINT\u00ae, Magnus Medical, Stanford University, or Neuro Wellness TMS Centers of America. SAINT\u2019s published results \u2014 including 78.6% remission in the 2022 randomized controlled trial \u2014 come only from SAINT\u00ae and should not be expected from any look-alike program.",
  keyTakeaways: [
    "Cognitive FX does not provide SAINT\u00ae. Its 5-day \u201cfMRI-guided accelerated TMS\u201d program is a different treatment.",
    "Cognitive FX states it is \u201cnot affiliated with, endorsed by or licensed by Stanford University or Magnus Medical\u201d and \u201cdoes not use Magnus Medical equipment or software.\u201d",
    "SAINT\u2019s 78.6% remission result (Cole et al., 2022) comes only from SAINT\u00ae on the FDA-cleared Magnus system \u2014 not from any \u201calternative\u201d or \u201cfMRI-guided\u201d program.",
    "Copying SAINT\u2019s 5-day schedule does not copy the FDA-cleared targeting the trials tested.",
    "Neuro Wellness in Coral Springs, Florida is the verified SAINT\u00ae provider listed on Magnus Medical\u2019s locator.",
  ],
  sections,
  faqs,
  sources,
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/saint-disclaimer#webpage`,
        name: "SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae",
        url: `${SITE_URL}/learn/saint-disclaimer`,
        description:
          'Cognitive FX and other "fMRI-guided accelerated TMS" programs are not SAINT\u00ae and are not affiliated with it. SAINT\u00ae results don\u2019t apply to them. Read the facts.',
        reviewedBy: {
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        lastReviewed: "2026-11-03",
      }),
      medicalClinicNode,
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/saint-disclaimer#faq`),
      breadcrumbJsonld(baseCrumbs("SAINT\u00ae Disclaimer")),
      {
        "@type": "Article",
        "@id": `${SITE_URL}/learn/saint-disclaimer#article`,
        headline:
          "SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae",
        description:
          'Cognitive FX and other "fMRI-guided accelerated TMS" programs are not SAINT\u00ae and are not affiliated with it. SAINT\u00ae results don\u2019t apply to them. Read the facts.',
        datePublished: "2026-11-03",
        dateModified: "2026-11-03",
        mainEntityOfPage: `${SITE_URL}/learn/saint-disclaimer`,
        author: {
          "@type": "MedicalOrganization",
          name: "Neuro Wellness TMS Centers of America",
        },
        reviewedBy: {
          "@type": "Person",
          name: "Daniel Pietras, MD",
          url: `${SITE_URL}/dr-daniel-pietras-md`,
        },
        publisher: {
          "@type": "MedicalOrganization",
          name: "Neuro Wellness TMS Centers of America",
          url: `${SITE_URL}/`,
        },
      },
    ],
  },
  relatedLinks,
  reviewBlock,
  toc: [
    { label: "The short version", href: "#the-short-version" },
    {
      label: "Cognitive FX in its own words",
      href: "#cognitive-fx-is-not-saint-in-its-own-words",
    },
    {
      label: "What the labels don\u2019t mean",
      href: "#what-alternative-equivalent-and-fmri-guided-do-not-mean",
    },
    { label: "Results disclaimer", href: "#results-disclaimer" },
    {
      label: "Price disclaimer",
      href: "#price-disclaimer-the-prices-cant-be-compared",
    },
    {
      label: "Non-affiliation & trademarks",
      href: "#our-non-affiliation-and-trademark-statement",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. SAINT\u00ae is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "Cognitive FX is a trademark of its owner. SAINT\u00ae is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Neuro Wellness TMS Centers of America is a verified SAINT\u00ae provider and is not affiliated with, endorsed by, or connected to Cognitive FX. Quotes from Cognitive FX published materials were accessed November 3, 2026.",
  ctaBand: {
    headline: "See if real SAINT\u00ae is a fit, free",
    primaryText: "See if I may be a candidate",
    primaryHref: "/learn/who-qualifies-for-saint-tms",
    secondaryText: "Talk to a real person",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
};
